import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import Drawer from '../../components_v2/Drawer/Drawer'
import { axios } from 'api'
import { getCountryCode } from 'helpers/getCountryCode'
import { community_event_location_filtered_event } from 'gtm/pushEvents'
import {
  countryGroupKey,
  groupRegionsByCountry,
  isSameRegionSelection,
  matchesUserCountry,
  regionFilterLabel,
  stateDisplayLabel
} from './countryFilter'

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  text-align: left;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Heading = styled.h5`
  margin: 0;
  color: #000;
  font-family: 'Proxima-Nova';
  font-size: 18px;
  font-weight: 600;
  line-height: 1.35;
  text-transform: uppercase;
`

const SearchWrapper = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
`

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 1px solid #a3a3a3;
  border-right: none;
  outline: none;
  padding: 12px 16px;
  color: #3b3b3b;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.352px;

  &::placeholder {
    color: #636363;
  }
`

const SearchButton = styled.button`
  width: 48px;
  flex-shrink: 0;
  border: none;
  background: #000;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

const RegionList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 20rem);
  overflow-y: auto;
  @media screen and (min-width: 768px) {
    max-height: calc(100vh - 16rem);
  }
`

const RegionRow = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
  color: #3b3b3b;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  line-height: 24px;
  letter-spacing: 0.352px;

  &:hover {
    color: #000;
  }
`

const CountryGroup = styled.div`
  border-top: 1px solid #e8e8e8;

  &:first-of-type {
    border-top: none;
  }
`

const CountryTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: none;
  background: transparent;
  padding: 14px 0;
  cursor: pointer;
  text-align: left;
  font-family: 'Proxima-Nova';
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #000;
`

const CountryMeta = styled.span`
  font-weight: 400;
  color: #636363;
  text-transform: none;
  letter-spacing: 0.2px;
  font-size: 13px;
`

const Chevron = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
  transition: transform 0.15s ease;
`

const CountryRegions = styled.div`
  display: ${({ $expanded }) => ($expanded ? 'flex' : 'none')};
  flex-direction: column;
  gap: 8px;
  padding: 0 0 16px 4px;
`

const EmptyState = styled.p`
  margin: 0;
  padding: 0;
  color: #636363;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  line-height: 24px;
`

const buildDefaultExpandedKeys = ({
  groups,
  visitorCountryCode,
  selectedRegion,
  expandAll
}) => {
  const keys = new Set()
  if (expandAll) {
    groups.forEach((group) => keys.add(group.key))
    return keys
  }

  groups.forEach((group) => {
    if (
      visitorCountryCode &&
      matchesUserCountry(group.countryRaw, visitorCountryCode)
    ) {
      keys.add(group.key)
    }
  })

  if (selectedRegion?.country) {
    keys.add(countryGroupKey(selectedRegion.country))
  }

  if (keys.size === 0 && groups[0]) {
    keys.add(groups[0].key)
  }

  return keys
}

const LocationDrawer = ({
  isOpen,
  onClose,
  selectedRegion,
  onSelectRegion
}) => {
  const [regions, setRegions] = useState([])
  const [visitorCountryCode, setVisitorCountryCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [expandedKeys, setExpandedKeys] = useState(() => new Set())

  useEffect(() => {
    if (!isOpen) {
      setQuery('')
      return undefined
    }

    let cancelled = false
    setIsLoading(true)

    Promise.all([axios.get('/events/regions'), getCountryCode()])
      .then(([regionsRes, countryCode]) => {
        if (cancelled) return
        setRegions(regionsRes.data?.regions || [])
        setVisitorCountryCode(countryCode || '')
      })
      .catch((err) => {
        console.error(err)
        if (!cancelled) {
          setRegions([])
          setVisitorCountryCode('')
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen])

  const normalizedQuery = query.trim().toLowerCase()
  const filteredRegions = useMemo(() => {
    if (!normalizedQuery) return regions
    return regions.filter((entry) => {
      const label = regionFilterLabel(entry).toLowerCase()
      const state = stateDisplayLabel(entry).toLowerCase()
      return label.includes(normalizedQuery) || state.includes(normalizedQuery)
    })
  }, [normalizedQuery, regions])

  const countryGroups = useMemo(
    () => groupRegionsByCountry(filteredRegions, visitorCountryCode),
    [filteredRegions, visitorCountryCode]
  )

  useEffect(() => {
    if (!isOpen) return
    setExpandedKeys(
      buildDefaultExpandedKeys({
        groups: countryGroups,
        visitorCountryCode,
        selectedRegion,
        expandAll: Boolean(normalizedQuery)
      })
    )
  }, [
    isOpen,
    countryGroups,
    visitorCountryCode,
    selectedRegion,
    normalizedQuery
  ])

  const toggleCountry = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleSelect = (regionEntry) => {
    onSelectRegion?.(regionEntry)
    community_event_location_filtered_event(
      regionEntry?.region ? regionFilterLabel(regionEntry) : null
    )
    setQuery('')
    onClose()
  }

  const isActive = (entry) => isSameRegionSelection(entry, selectedRegion)

  return (
    <Drawer isOpen={isOpen} title='State' onClose={onClose}>
      <Body>
        <Section>
          <Heading>Find Events</Heading>
          <SearchWrapper>
            <SearchInput
              type='text'
              placeholder='Search by state or country'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <SearchButton type='button' aria-label='Search' onClick={() => {}}>
              <svg
                width='32'
                height='32'
                viewBox='0 0 24 24'
                fill='none'
                stroke='white'
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <polyline points='9 18 15 12 9 6' />
              </svg>
            </SearchButton>
          </SearchWrapper>
        </Section>

        <Section>
          <RegionList>
            <RegionRow
              active={!selectedRegion}
              onClick={() => handleSelect(null)}
            >
              All states
            </RegionRow>

            {isLoading ? (
              <EmptyState>Loading states...</EmptyState>
            ) : countryGroups.length === 0 ? (
              <EmptyState>No states found.</EmptyState>
            ) : (
              countryGroups.map((group) => {
                const expanded = expandedKeys.has(group.key)
                return (
                  <CountryGroup key={group.key}>
                    <CountryTrigger
                      type='button'
                      aria-expanded={expanded}
                      onClick={() => toggleCountry(group.key)}
                    >
                      <span>
                        {group.countryLabel}
                        <CountryMeta> ({group.regions.length})</CountryMeta>
                      </span>
                      <Chevron $expanded={expanded} aria-hidden>
                        <svg
                          width='20'
                          height='20'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        >
                          <polyline points='9 18 15 12 9 6' />
                        </svg>
                      </Chevron>
                    </CountryTrigger>
                    <CountryRegions $expanded={expanded}>
                      {group.regions.map((entry) => (
                        <RegionRow
                          key={`${entry.region}-${entry.country}`}
                          active={isActive(entry)}
                          onClick={() => handleSelect(entry)}
                        >
                          {stateDisplayLabel(entry)}
                        </RegionRow>
                      ))}
                    </CountryRegions>
                  </CountryGroup>
                )
              })
            )}
          </RegionList>
        </Section>
      </Body>
    </Drawer>
  )
}

export default LocationDrawer
